import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  Pill,
  Loader2,
  CheckCircle,
  Package,
  Clock,
  ChevronDown,
  ChevronRight,
  Activity,
  Building,
  MessageSquare,
  History,
  Plus,
  Send,
  Thermometer,
  AlertTriangle,
  Boxes,
  ArrowRightLeft,
  FileText,
  ExternalLink,
  Tag,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors">
        <div className="flex items-center gap-2 font-medium text-sm">
          <Icon className="h-4 w-4 text-[#E63946]" />
          {title}
          {badge && <Badge variant="outline" className="ml-2 text-xs">{badge}</Badge>}
        </div>
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

export const MedicationDetailPanel = ({ 
  medication, 
  isOpen, 
  onClose,
  onUpdate
}) => {
  const { t } = useTranslation('medications');
  const [activeTab, setActiveTab] = useState('overview');
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [orderAssignments, setOrderAssignments] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Fetch data when medication changes
  useEffect(() => {
    if (medication?.id && isOpen) {
      fetchNotes();
      fetchMovements();
      fetchOrderAssignments();
    }
  }, [medication?.id, isOpen]);

  const fetchNotes = async () => {
    setLoadingNotes(true);
    try {
      const response = await axios.get(`${API_URL}/api/medications/catalog/${medication.id}/notes`);
      setNotes(response.data || []);
    } catch (error) {
      // Notes endpoint may not exist yet, silently fail
      console.log('Notes not available:', error);
      setNotes([]);
    }
    setLoadingNotes(false);
  };

  const fetchMovements = async () => {
    setLoadingMovements(true);
    try {
      const response = await axios.get(`${API_URL}/api/inventory/movements`, {
        params: { medication_id: medication.id, limit: 20 }
      });
      setMovements(response.data || []);
    } catch (error) {
      console.log('Movements not available:', error);
      setMovements([]);
    }
    setLoadingMovements(false);
  };

  const fetchOrderAssignments = async () => {
    setLoadingOrders(true);
    try {
      // Search orders that contain this medication
      const response = await axios.get(`${API_URL}/api/orders`, {
        params: { medication_id: medication.id, limit: 20 }
      });
      setOrderAssignments(response.data?.orders || []);
    } catch (error) {
      console.log('Order assignments not available:', error);
      setOrderAssignments([]);
    }
    setLoadingOrders(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setSavingNote(true);
    try {
      await axios.post(`${API_URL}/api/medications/catalog/${medication.id}/notes`, {
        content: newNote
      });
      setNewNote('');
      fetchNotes();
      toast.success('Nota agregada');
    } catch (error) {
      // If endpoint doesn't exist, save locally
      const localNote = {
        id: Date.now(),
        content: newNote,
        created_at: new Date().toISOString(),
        created_by: 'Usuario'
      };
      setNotes([localNote, ...notes]);
      setNewNote('');
      toast.success('Nota agregada localmente');
    }
    setSavingNote(false);
  };

  if (!medication) return null;

  const stockStatus = medication.stock_quantity <= 0 ? 'out' :
    medication.stock_quantity <= (medication.min_stock_level || 10) ? 'low' : 'ok';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl md:max-w-2xl overflow-hidden flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <Pill className="h-7 w-7 text-[#E63946]" />
            </div>
            <div className="flex-1">
              <SheetTitle className="text-lg font-semibold">
                {medication.generic_name}
              </SheetTitle>
              {medication.commercial_name && (
                <p className="text-sm text-muted-foreground">{medication.commercial_name}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline">{medication.concentration}</Badge>
                <Badge variant="outline">{medication.form}</Badge>
                {medication.is_controlled && (
                  <Badge variant="destructive">Controlado</Badge>
                )}
                {medication.requires_refrigeration && (
                  <Badge className="bg-blue-100 text-blue-800">
                    <Thermometer className="h-3 w-3 mr-1" />
                    Refrigerar
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Stock indicator */}
          <div className={`mt-4 p-3 rounded-lg flex items-center justify-between ${
            stockStatus === 'out' ? 'bg-red-50 border border-red-200' :
            stockStatus === 'low' ? 'bg-yellow-50 border border-yellow-200' :
            'bg-green-50 border border-green-200'
          }`}>
            <div className="flex items-center gap-2">
              <Boxes className={`h-5 w-5 ${
                stockStatus === 'out' ? 'text-red-600' :
                stockStatus === 'low' ? 'text-yellow-600' :
                'text-green-600'
              }`} />
              <span className="font-medium">{t('detail.currentStock')}</span>
            </div>
            <div className="text-right">
              <span className={`text-2xl font-bold ${
                stockStatus === 'out' ? 'text-red-600' :
                stockStatus === 'low' ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {medication.stock_quantity || 0}
              </span>
              <span className="text-sm text-muted-foreground ml-1">{medication.unit || t('detail.units')}</span>
              {stockStatus === 'low' && (
                <p className="text-xs text-yellow-600">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  {t('detail.stockStatus.low')} ({t('detail.minStock')}: {medication.min_stock_level || 10})
                </p>
              )}
              {stockStatus === 'out' && (
                <p className="text-xs text-red-600">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  {t('detail.stockStatus.out')}
                </p>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Activity className="h-4 w-4 mr-1" />
              {t('detail.tabs.overview')}
            </TabsTrigger>
            <TabsTrigger value="notes" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <MessageSquare className="h-4 w-4 mr-1" />
              {t('detail.tabs.notes')}
            </TabsTrigger>
            <TabsTrigger value="movements" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <ArrowRightLeft className="h-4 w-4 mr-1" />
              {t('detail.tabs.movements')}
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <FileText className="h-4 w-4 mr-1" />
              {t('detail.tabs.orders')}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            {/* Overview Tab */}
            <TabsContent value="overview" className="p-6 space-y-4 mt-0">
              {/* Basic Info */}
              <CollapsibleSection title={t('detail.basicInfo')} icon={Pill} defaultOpen={true}>
                <div className="space-y-3 p-3 bg-white rounded-lg border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.genericName')}</Label>
                      <p className="font-medium">{medication.generic_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.commercialName')}</Label>
                      <p className="font-medium">{medication.commercial_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.activeIngredient')}</Label>
                      <p className="font-medium">{medication.active_ingredient}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.concentration')}</Label>
                      <p className="font-medium">{medication.concentration}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.pharmaceuticalForm')}</Label>
                      <p className="font-medium">{medication.form}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.manufacturer')}</Label>
                      <p className="font-medium">{medication.manufacturer || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Inventory Info */}
              <CollapsibleSection title={t('detail.inventory')} icon={Boxes} defaultOpen={true}>
                <div className="space-y-3 p-3 bg-white rounded-lg border">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.currentStock')}</Label>
                      <p className="font-medium text-lg">{medication.stock_quantity || 0} {medication.unit || t('detail.units')}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.minStock')}</Label>
                      <p className="font-medium">{medication.min_stock_level || 10} {medication.unit || t('detail.units')}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.location')}</Label>
                      <p className="font-medium">{medication.location || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">{t('detail.lastPurchasePrice')}</Label>
                      <p className="font-medium">
                        {medication.last_purchase_price ? `$${medication.last_purchase_price.toLocaleString()}` : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Special Conditions */}
              <CollapsibleSection title={t('detail.specialConditions')} icon={AlertTriangle} defaultOpen={false}>
                <div className="space-y-2 p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <input 
                      type="checkbox" 
                      checked={medication.is_controlled} 
                      disabled 
                      className="rounded" 
                    />
                    <span>{t('detail.controlledMedication')}</span>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                    <input 
                      type="checkbox" 
                      checked={medication.requires_refrigeration} 
                      disabled 
                      className="rounded" 
                    />
                    <span>{t('detail.requiresRefrigeration')}</span>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Alternative Names */}
              {medication.alternative_names && medication.alternative_names.length > 0 && (
                <CollapsibleSection 
                  title={t('detail.alternativeNames')} 
                  icon={Tag} 
                  defaultOpen={false}
                  badge={medication.alternative_names.length}
                >
                  <div className="p-3 bg-white rounded-lg border">
                    <div className="flex flex-wrap gap-2">
                      {medication.alternative_names.map((name, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-blue-100 text-blue-800">
                          {name}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('detail.alternativeNamesHelp')}
                    </p>
                  </div>
                </CollapsibleSection>
              )}
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="p-6 space-y-4 mt-0">
              <h3 className="font-semibold flex items-center gap-2 mb-4">
                <MessageSquare className="h-5 w-5 text-[#E63946]" />
                {t('detail.medicationNotes')}
              </h3>

              {/* Add Note Form */}
              <div className="bg-white rounded-lg border p-4">
                <Label className="text-sm mb-2 block">{t('detail.addNote')}</Label>
                <div className="flex gap-2">
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={t('detail.notePlaceholder')}
                    className="flex-1 min-h-[80px]"
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button 
                    onClick={handleAddNote} 
                    disabled={!newNote.trim() || savingNote}
                    size="sm"
                  >
                    {savingNote ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                    {t('detail.addNoteBtn')}
                  </Button>
                </div>
              </div>

              {/* Notes List */}
              {loadingNotes ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#E63946]" />
                </div>
              ) : notes.length > 0 ? (
                <div className="space-y-3">
                  {notes.map((note, idx) => (
                    <div key={note.id || idx} className="bg-white rounded-lg border p-4">
                      <p className="text-sm">{note.content}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {new Date(note.created_at).toLocaleString()}
                        {note.created_by && <span>• {note.created_by}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>{t('detail.noNotes')}</p>
                </div>
              )}
            </TabsContent>

            {/* Movements Tab */}
            <TabsContent value="movements" className="p-6 space-y-4 mt-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <ArrowRightLeft className="h-5 w-5 text-[#E63946]" />
                  {t('detail.inventoryMovements')}
                </h3>
                <Button variant="outline" size="sm" asChild>
                  <a href="/enterprise-inventory?tab=movements" target="_blank">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    {t('detail.viewAll')}
                  </a>
                </Button>
              </div>

              {loadingMovements ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#E63946]" />
                </div>
              ) : movements.length > 0 ? (
                <div className="space-y-2">
                  {movements.map((mov, idx) => (
                    <div key={mov.id || idx} className={`flex items-center justify-between p-3 rounded-lg border ${
                      mov.movement_type === 'in' || mov.movement_type === 'purchase' || mov.movement_type === 'adjustment_in'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          mov.movement_type === 'in' || mov.movement_type === 'purchase' || mov.movement_type === 'adjustment_in'
                            ? 'bg-green-200'
                            : 'bg-red-200'
                        }`}>
                          <ArrowRightLeft className={`h-4 w-4 ${
                            mov.movement_type === 'in' || mov.movement_type === 'purchase' || mov.movement_type === 'adjustment_in'
                              ? 'text-green-700'
                              : 'text-red-700'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{mov.movement_type}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(mov.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`font-bold ${
                          mov.movement_type === 'in' || mov.movement_type === 'purchase' || mov.movement_type === 'adjustment_in'
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {mov.movement_type === 'in' || mov.movement_type === 'purchase' || mov.movement_type === 'adjustment_in'
                            ? '+' : '-'}{mov.quantity}
                        </span>
                        {mov.notes && <p className="text-xs text-muted-foreground">{mov.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ArrowRightLeft className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>{t('detail.noMovements')}</p>
                  <Button variant="outline" size="sm" className="mt-4" asChild>
                    <a href="/enterprise-inventory?tab=movements">
                      {t('detail.goToInventory')}
                    </a>
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders" className="p-6 space-y-4 mt-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#E63946]" />
                  {t('detail.ordersWithMedication')}
                </h3>
              </div>

              {loadingOrders ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#E63946]" />
                </div>
              ) : orderAssignments.length > 0 ? (
                <div className="space-y-2">
                  {orderAssignments.map((order, idx) => (
                    <div key={order.id || idx} className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-[#E63946]/10 flex items-center justify-center">
                          <FileText className="h-5 w-5 text-[#E63946]" />
                        </div>
                        <div>
                          <p className="font-medium">Orden #{order.id?.slice(0, 8)}</p>
                          <p className="text-sm text-muted-foreground">
                            {order.patient_name || 'Paciente'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{order.status}</Badge>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={`/orders?view=${order.id}`}>
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>{t('detail.noOrderAssignments')}</p>
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
};

export default MedicationDetailPanel;
